"use client"

import styled from "@emotion/styled"
import { Box, IconButton } from "@mui/material"
import { Image } from "next/dist/client/image-component"
import { Desktop, Mobile, NewText } from "ui"
import { extractProperty, extractPropertyMediaItems, getUmbracoMediaUrl } from "utils"
import LandingLeadForm from "./LandingLeadForm"

const StyledFormWrapper = styled.div`
  background-color: #ededeb;
  height: 100%;
  color: ${(props) => props.theme.colors.black};
  ${(props) => props.theme.breakpoints.up("md")} {
    max-height: 100vh;
    overflow-y: auto;
  }
`

const StyledImageSwapper = styled.div`
  position: relative;
  width: 150px;
  height: 181px;
  margin: 8px auto;
`

const StyledTitlesWrapper = styled(Box)`
  padding: 0px 29px 0px 21px;
  margin-bottom: 16px;
`

const StyledBottomLogosWrapper = styled(Box)`
  padding: 23px 28px 32px 21.11px;
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  flex-wrap: wrap;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 48px 24px 24px;
    display: none;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    display: flex;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 48px 43px 24px 40px;
  }
`

const StyledBottomLogos = styled(Image)`
  height: 50px;
  min-width: 95px;
  width: fit-content;
  object-fit: contain;
  object-position: left;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 68px;
    min-width: 158px;
    object-position: center;
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  margin-left: auto;
  gap: 8px;
  padding-top: 16px;
  padding-right: 16px;
  width: fit-content;
  position: sticky;
  top: 0;
`

const StyledIconButton = styled(IconButton)`
  padding: 0;
`

type Props = {
  locale: string
  data: any
  companyId?: number
  companyName?: string
  messages?: any
}

export const LandingFormSide: React.FC<Props> = ({ data, messages, locale }: Props) => {
  const pageLogo = extractProperty(data, "pageLogo")
  const pageTitle = extractProperty(data, "pageTitle")
  const shortDescription = extractProperty(data, "shortDescription")
  const bottomLogos = extractPropertyMediaItems(data, "bottomLogos")
  const pageForm = extractProperty(data, "pageForm")?.[0]?.properties
  const bmbyId = pageForm?.find((property: any) => property.propertyName === "bmbyId")
    ?.propertyValue?.value
  const formTitle = pageForm?.find((property: any) => property.propertyName === "fromTitle")
    ?.propertyValue?.value
  const formTextForMeiling = pageForm?.find(
    (property: any) => property.propertyName === "formTextForMeiling",
  )?.propertyValue?.value
  const formTextForSms = pageForm?.find(
    (property: any) => property.propertyName === "formTextForSms",
  )?.propertyValue?.value
  const formTextForTerms = pageForm
    ?.find((property: any) => property.propertyName === "formTextForTerms")
    ?.propertyValue?.richText?.replace(
      `/${locale === "he" ? "he-il" : "en-us"}/properties/pages`,
      locale === "he" ? "" : "/en",
    )

  const handleWhatsAppClick = () => {
    const phoneNumber = "+972765997044"
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, "")}`
    window.open(whatsappUrl, "_blank")
  }

  const handlePhoneClick = () => {
    window.location.href = "tel:*6635"
  }

  return (
    <StyledFormWrapper id="landingLeadForm">
      <ButtonsContainer>
        <StyledIconButton onClick={handleWhatsAppClick} aria-label="Contact via WhatsApp">
          <Image src={"/rishoGreenPhone.png"} alt="WhatsApp Contact" width={40} height={40} />
        </StyledIconButton>
        <StyledIconButton onClick={handlePhoneClick} aria-label="Call *6635">
          <Image src={"/rishoBlackPhone.png"} alt="Phone Call" width={40} height={40} />
        </StyledIconButton>
      </ButtonsContainer>
      <Desktop>
        <StyledImageSwapper>
          <Image
            src={getUmbracoMediaUrl(pageLogo)}
            alt="page logo"
            width={500}
            height={500}
            style={{
              height: "100%",
              width: "100%",
              objectFit: "cover",
            }}
            priority={true}
          />
        </StyledImageSwapper>
        <StyledTitlesWrapper>
          <NewText
            component={"h1"}
            desktopFontSize={35}
            desktopLineHeight={"61.65px"}
            desktopFontWeight={500}
            inheritColor
            textAlign={"center"}
            letterSpacing={"-0.01em"}
          >
            {pageTitle}
          </NewText>
          <NewText
            component={"h2"}
            desktopFontSize={20}
            desktopLineHeight={"24.2px"}
            desktopFontWeight={500}
            inheritColor
            marginTop={"1px"}
            textAlign={"center"}
            letterSpacing={"-0.015em"}
          >
            {shortDescription}
          </NewText>
        </StyledTitlesWrapper>
      </Desktop>
      <LandingLeadForm
        messages={messages}
        bmbyId={bmbyId}
        title={formTitle}
        formTextForMeiling={formTextForMeiling}
        formTextForTerms={formTextForTerms}
        formTextForSms={formTextForSms}
      />
      <StyledBottomLogosWrapper>
        {bottomLogos?.map((logo: string, key: number) => (
          <StyledBottomLogos
            key={key}
            src={getUmbracoMediaUrl(logo)}
            alt="footer logos"
            width={500}
            height={500}
            priority={true}
          />
        ))}
        <Mobile sx={{ width: "100%" }}>
          <Box
            component="a"
            href="tel:*6635"
            sx={{
              textDecoration: "none",
              color: "inherit",
              display: "block",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              backgroundColor: "#f5f5f5",
              marginBottom: "16px",
              textAlign: "center",
              "&:hover": {
                backgroundColor: "#e0e0e0",
              },
            }}
          >
            <NewText
              component={"span"}
              desktopFontSize={16}
              desktopLineHeight={"20px"}
              letterSpacing={"-0.01em"}
              inheritColor
              sx={{
                fontWeight: 600,
              }}
            >
              📞 *6635
            </NewText>
          </Box>
          <NewText
            component={"p"}
            desktopFontSize={12.5}
            desktopLineHeight={"15px"}
            letterSpacing={"-0.01em"}
            inheritColor
            width={"100%"}
            marginTop={"23.6px"}
          >
            * הדמיות להמחשה בלבד. ט.ל.ח.
          </NewText>
        </Mobile>
      </StyledBottomLogosWrapper>
    </StyledFormWrapper>
  )
}

export default LandingFormSide
